import { useLayoutEffect } from 'react';
import type { StoreApi, UseBoundStore } from 'zustand';

import {
  getSingleBillCurrencyCodeByCountry,
  getSingleBillPaymentConfigurationBucket,
  getSingleBillPaymentElementConfigurationBucket,
} from '@ctct/state-server-layer';

interface SingleBillBillingCountryState {
  isDisplayingCard: boolean;
  paymentElementConfigurationBucket: string | null;
  paymentElementCountryBucket: string | null;
  selectedBillingCountry: string | null;
  selectedBillingCurrency: string | null;
}

const hasSingleBillInfoFormData = (data: unknown): data is { isEditing: () => boolean } => {
  return typeof data === 'object' && data !== null && 'isEditing' in data && typeof data.isEditing === 'function';
};

interface SingleBillBillingCountryParams {
  countries?: { getCountries: () => { code: string; display: string }[] };
  isStripeSingleBillEnabled?: boolean;
  pageStore: UseBoundStore<StoreApi<SingleBillBillingCountryState>>;
  singleBill?: { getCountry?: () => string | null; getCurrencyCd?: () => string | null };
  singleBillInfoFormWidgetProps: { status: string; data?: unknown };
}

export const useSingleBillBillingCountry = ({
  countries,
  pageStore,
  singleBill,
  singleBillInfoFormWidgetProps,
}: SingleBillBillingCountryParams) => {
  const selectedBillingCountry = pageStore((state) => state.selectedBillingCountry);
  const isDisplayingBillingInfoCard = pageStore((state) => state.isDisplayingCard);
  const isSingleBillInfoFormLoaded = singleBillInfoFormWidgetProps.status === 'loaded';
  const isEditingSingleBill =
    isSingleBillInfoFormLoaded &&
    hasSingleBillInfoFormData(singleBillInfoFormWidgetProps.data) &&
    singleBillInfoFormWidgetProps.data.isEditing() === true;

  useLayoutEffect(() => {
    if (!singleBill || !countries) return;

    const shouldResetToSavedBillingInfo = isEditingSingleBill && isDisplayingBillingInfoCard;

    if (selectedBillingCountry && !shouldResetToSavedBillingInfo) return;

    const defaultCountry = singleBill.getCountry?.() || countries.getCountries()[0]?.code || null;

    if (defaultCountry) {
      const defaultCurrency =
        singleBill.getCurrencyCd?.() || getSingleBillCurrencyCodeByCountry(defaultCountry) || null;

      pageStore.setState({
        selectedBillingCountry: defaultCountry,
        selectedBillingCurrency: defaultCurrency,
        paymentElementCountryBucket: getSingleBillPaymentConfigurationBucket(defaultCountry),
        paymentElementConfigurationBucket: getSingleBillPaymentElementConfigurationBucket(
          defaultCountry,
          defaultCurrency
        ),
      });
    }
  }, [countries, isDisplayingBillingInfoCard, isEditingSingleBill, pageStore, selectedBillingCountry, singleBill]);
};
